import { useEffect, useState } from 'react';
import { CalendarDays, AlertCircle, MapPin, Ticket } from 'lucide-react';
import { getOverview, Overview, WEEKDAYS, WEEKDAYS_SHORT, hhmm, money } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { fmtEventDate } from '../../events/api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { colorClase } from '../../../utils/classColor';

export default function StudentHome() {
  const [d, setD] = useState<Overview | null>(null);
  useEffect(() => { getOverview().then(setD).catch(() => {}); }, []);

  if (!d) return <div className="space-y-4"><Skeleton className="h-28 rounded-2xl" /><Skeleton className="h-48 rounded-2xl" /></div>;

  const p = d.profile;
  // Agrupar horario por día
  const byDay: Record<number, typeof d.schedule> = {};
  for (const s of d.schedule) (byDay[s.weekday] = byDay[s.weekday] || []).push(s);
  const days = Object.keys(byDay).map(Number).sort();

  return (
    <div className="space-y-5">
      {/* Saludo */}
      <div className="flex items-center gap-3 rounded-2xl border border-line bg-surface p-5 shadow-sm">
        {/* PORTAL DEL ALUMNO — sin insignia de adeudo (decisión de Carlos 7-ago-2026: que los
            demás no la vean). Hoy además sería imposible: el backend no le da las banderas a un
            alumno. Si Karla algún día quiere que cada quien vea SU aviso, se quita este flag
            y se abre el permiso; el bloque "Adeudo" de más abajo ya le dice cuánto debe. */}
        <PersonAvatar contactId={p.account_id} hasPhoto={p.has_photo}
          name={p.firstname} lastName={p.lastname}
          size="h-14 w-14" initialClass="text-lg" showDebt={false} />
        <div className="min-w-0">
          <h1 className="font-display text-xl font-bold text-content">¡Hola, {p.firstname}! 👋</h1>
          {p.membership_name && (
            <span className="mt-1 inline-block rounded-full bg-brand-blue/10 px-2.5 py-0.5 text-xs font-medium text-brand-blue">{p.membership_name}</span>
          )}
        </div>
      </div>

      {/* Adeudo pendiente */}
      {d.debt.count > 0 && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4">
          <AlertCircle className="shrink-0 text-amber-600 dark:text-amber-400" size={20} />
          <div className="text-sm">
            <div className="font-semibold text-content">Tienes un pago pendiente</div>
            <div className="text-content-2">{money(d.debt.total)} en {d.debt.count} concepto(s). Pasa a recepción para regularizarlo.</div>
          </div>
        </div>
      )}

      {/* Próximos eventos */}
      {d.events.length > 0 && d.events.map((ev) => (
        <div key={ev.event_id} className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
          <div className="relative h-28 w-full bg-surface-2">
            {ev.image_url
              ? <img src={ev.image_url} alt="" className="h-full w-full object-cover" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
              : <div className="h-full w-full" style={{ background: `linear-gradient(135deg, ${ev.event_type_color ? `#${ev.event_type_color}` : 'rgb(var(--c-accent))'}, rgb(var(--c-blue)))` }} />}
            <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white"><Ticket size={11} /> Tu lugar confirmado</span>
          </div>
          <div className="p-4">
            <div className="font-display font-bold text-content">{ev.name}</div>
            <div className="mt-0.5 text-sm capitalize text-content-2">{fmtEventDate(ev.datetime)}</div>
            {ev.venue && <div className="mt-0.5 flex items-center gap-1 text-xs text-content-3"><MapPin size={12} /> {ev.venue}</div>}
          </div>
        </div>
      ))}

      {/* Mi horario semanal */}
      <div>
        <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-content-2"><CalendarDays size={16} /> Mi horario</h2>
        {days.length === 0 ? (
          <EmptyState icon={CalendarDays} title="Sin clases asignadas" subtitle="Habla con recepción para inscribirte." compact />
        ) : (
          <div className="space-y-3">
            {days.map((wd) => (
              <div key={wd} className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-3">{WEEKDAYS[wd] || WEEKDAYS_SHORT[wd]}</div>
                <div className="space-y-2">
                  {byDay[wd].map((s, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="h-8 w-1.5 rounded-full" style={{ background: colorClase(s.color) }} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-content">{s.name}</div>
                        {s.classroom && <div className="text-xs text-content-3">{s.classroom}</div>}
                      </div>
                      <div className="shrink-0 text-sm tabular-nums text-content-2">{hhmm(s.start_time)}–{hhmm(s.end_time)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
